import { useState } from 'react';
import type { FocusItem } from '@ehr/shared';
import { Modal } from './Modal.js';
import { RemoveScopeModal, type RemoveScope } from './RemoveScopeModal.js';

export type FocusChoice = FocusItem & { on: boolean };

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V108, V111, V128, V131): the chips of Problems, goals, and
 * objectives addressed. Every problem, goal and objective on the linked plan shows, none picked
 * until tapped; a picked chip has an X that asks This note only / This client going forward / All
 * clients going forward; "+ Add" types in an item of your own. A locked note lists what was picked.
 * ehr#908: the plan's goals and objectives are listed once, in Treatment plan goals, where a status
 * marks them addressed; `note` says so (and how many are), above the chips.
 */
export function FocusChips({
  choices,
  locked,
  onToggle,
  onAsk,
  onAdd,
  note,
}: {
  choices: readonly FocusChoice[];
  locked: boolean;
  onToggle: (c: FocusChoice) => void;
  onAsk: (c: FocusChoice) => void;
  onAdd: () => void;
  note?: string;
}) {
  const shown = locked ? choices.filter((c) => c.on) : choices;
  const noteLine = note ? (
    <p className="muted pn-focus-note" data-test="focus-goals-note">
      {note}
    </p>
  ) : null;
  if (locked && !shown.length)
    return (
      noteLine ?? (
        <p className="muted pn-focus-none" data-test="focus-none">
          None picked
        </p>
      )
    );
  return (
    <div className="pn-focus" data-test="focus-chips">
      {noteLine}
      {!locked && !choices.length && !note && (
        <p className="muted pn-focus-none">
          No treatment plan items to pick. Use + Add to name what this session worked on.
        </p>
      )}
      {shown.map((c) => (
        <span key={c.key} className={`pn-focus-w${c.on ? ' on' : ''}`}>
          <button
            type="button"
            className="pn-focus-chip"
            aria-pressed={c.on}
            disabled={locked}
            title={c.kind === 'custom' ? 'Your own item' : `From the plan (${c.kind})`}
            onClick={() => onToggle(c)}
          >
            {c.label}
          </button>
          {c.on && !locked && (
            <button
              type="button"
              className="pn-focus-x"
              aria-label={`Remove ${c.label}`}
              onClick={() => onAsk(c)}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!locked && (
        <button
          type="button"
          className="pn-link pn-focus-add"
          aria-label="Add a problem, goal, or objective"
          onClick={onAdd}
        >
          + Add
        </button>
      )}
    </div>
  );
}

/** The chips with V141's two windows: Add a problem, goal, or objective, and Remove. */
export function FocusSection({
  choices,
  locked,
  firstName,
  onToggle,
  onAdd,
  onRemove,
  note,
}: {
  choices: readonly FocusChoice[];
  locked: boolean;
  firstName: string;
  /** ehr#908: the line about the goals addressed in Treatment plan goals (see FocusChips). */
  note?: string;
  onToggle: (c: FocusChoice) => void;
  /** Typed in "+ Add" (trimmed, not empty). */
  onAdd: (label: string) => void;
  onRemove: (c: FocusChoice, scope: RemoveScope) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [ask, setAsk] = useState<FocusChoice | null>(null);
  const save = () => {
    const v = text.replace(/\s+/g, ' ').trim();
    if (!v) {
      setErr('Type something first.');
      return;
    }
    setAdding(false);
    setText('');
    setErr('');
    onAdd(v);
  };
  return (
    <>
      <FocusChips
        choices={choices}
        locked={locked}
        onToggle={onToggle}
        onAsk={setAsk}
        note={note}
        onAdd={() => {
          setText('');
          setErr('');
          setAdding(true);
        }}
      />
      {adding && (
        <Modal
          title="Add a problem, goal, or objective"
          onClose={() => setAdding(false)}
          className="pn-phrase-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={save}>
                Add
              </button>
            </>
          }
        >
          <input
            className="input pn-focus-input"
            value={text}
            maxLength={120}
            autoFocus
            autoComplete="off"
            placeholder="Type it in"
            aria-label="Problem, goal, or objective"
            onChange={(e) => {
              setText(e.target.value);
              setErr('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
            }}
          />
          {err && (
            <p className="pn-inline-err" role="alert">
              {err}
            </p>
          )}
        </Modal>
      )}
      {ask && (
        <RemoveScopeModal
          title={`Remove "${ask.label}"?`}
          onClose={() => setAsk(null)}
          onPick={(scope) => onRemove(ask, scope)}
        >
          Saved and signed notes are not changed. This client going forward takes it off {firstName}
          &rsquo;s new notes. All clients going forward takes it off every client&rsquo;s new notes.
        </RemoveScopeModal>
      )}
    </>
  );
}
